import React, { useEffect, useRef, useState } from "react";
import RuleConfiguration from "./components/RuleConfiguration";
import TimeTriggerConfig from "./components/TimeTrigger";
import NotificationSettings from "./components/NotificationSettings";
import "./EscalationRuleBuilder.css";
import ActionsSection from "./components/ActionsSection";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import ConditionBuilder from "./components/ConditionBuilder";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import {
  loadEscalationGetDataRequest,
  loadEscalationRuleAddRequest,
  loadEscalationRuleUpdateRequest,
  resetEscalationRuleAdd,
  resetEscalationRuleUpdate,
} from "./redux/Actions";
import { useDispatch, useSelector } from "react-redux";
import { addErrorAlert } from "../../alert/Actions";
import { Field, Form, Formik } from "formik";
import FormikCheckboxField from "../../../shared/components/FormikCheckboxField";
import Loader from "../../../shared/components/Loader";
import { Col, Row } from "react-bootstrap";
import Breadcrumbs from "../../../shared/components/Breadcrumbs";
import {
  BACKTOLIST,

} from "../../../shared/constants/Constants";
import DefaultButton from "../../../shared/components/DefaultButton";
import { PrevNextButtons } from "../../../shared/components/PrevNextButtons";

export default function EscalationRuleBuilder() {
  const location = useLocation();

  const { ruleDetails, loading, error, updateSuccess, updateError } =
    useSelector((state) => state.escalationDataService);

  const { addSuccess, addError } = useSelector((state) => state.escalationRule);

  // Get data from navigation state
  const { id: paramId } = useParams();
  const { id: stateId, isEdit: stateIsEdit } = location.state || {};
  const routeId = paramId && paramId !== "add" ? paramId : null;
  const id = stateId || routeId;
  const isEdit = stateIsEdit ?? !!routeId; 

  const [executionMode, setExecutionMode] = useState("scheduled");
  const [isSubmitting, setIsSubmitting] = useState(false); // <--- new
  const formikRef = useRef();
  const ruleConfigRef = useRef();
  const timeTriggerRef = useRef();
  const notificationRef = useRef();
  const actionsRef = useRef();
  const conditionsRef = useRef();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Fetch data only if editing
  useEffect(() => {
    if (isEdit && id) {
      dispatch(loadEscalationGetDataRequest(id));
    }
  }, [dispatch, id, isEdit]);

  useEffect(() => {
    if (error) {
      dispatch(
        addErrorAlert(
          error?.message ||
            "Failed to load escalation rule details. Please try again."
        )
      );
    }
  }, [error, dispatch]);

  // If backend add/update reports an error, re-enable the Save button so user can try again
  useEffect(() => {
    if (addError || updateError) {
      setIsSubmitting(false);
    }
  }, [addError, updateError]);

  const handleSave = async () => {
    // prevent double submission
    if (isSubmitting) return;

    // disable Save button (prevents multiple client-side clicks)
    setIsSubmitting(true);

    try {
      const ruleData = await ruleConfigRef.current?.validateAndGetData();
      const timeData = await timeTriggerRef.current?.validateAndGetData();
      const notificationData = await notificationRef.current?.validateAndGetData();
      const actionsData = (await actionsRef.current?.validateAndGetData()) || [];
      const conditionsData = (await conditionsRef.current?.validateAndGetData()) || null;

      // If any validation failed, re-enable and show error
      if (
        !(
          ruleData?.isValid &&
          timeData?.isValid &&
          notificationData?.isValid &&
          actionsData?.isValid &&
          conditionsData?.isValid
        )
      ) {
        setIsSubmitting(false);
        dispatch(
          addErrorAlert(
            "Validation failed in one or more sections. Please check all required fields."
          )
        );
        return;
      }

      // Build payload (same as before)
      const finalPayload = {
        is_active: ruleData.data.is_active,
        rule_name: ruleData.data.ruleName,
        description: ruleData.data.description,
        execution_mode: ruleData.data.executionMode,
        admin_group_id: ruleData.data.selectedGroup
          ? Number(ruleData.data.selectedGroup)
          : null,
        assigned_to_id: ruleData.data.selectedUser
          ? Number(ruleData.data.selectedUser)
          : null,
        category_config_id:
          ruleData.data.category === "" ? null : Number(ruleData.data.category),
        ticket_type: ruleData.data.ticketType,
        status: ruleData.data.selectedStatus
          ? Number(ruleData.data.selectedStatus)
          : null,
        notify_assigned_admin: notificationData.data.notify_assigned_admin,
        notify_request_user: notificationData.data.notify_request_user,
        notify_assigned_admin_group: notificationData.data.notify_assigned_admin_group,
        notify_cc_recipients: false,
        notify_custom_recipient: notificationData.data.notify_custom_recipient,
        notify_custom_recipient_id: notificationData.data.notify_custom_recipient_id, 
        notification_method: notificationData.data.method,
        notification_subject: notificationData.data.subject,
        notification_body: notificationData.data.body,
        actions: actionsData.data,
        additional_conditions: conditionsData?.additional_conditions || null,
      };

      if (
        ruleData.data.executionMode === "scheduled" &&
        timeData.data.enabled
      ) {
        finalPayload.time_trigger_enabled = timeData.data.enabled;
        finalPayload.time_trigger_hours = timeData.data.hours;
        finalPayload.time_trigger_minutes = timeData.data.minutes;
        finalPayload.time_trigger_reference = timeData.data.referenceTime;
        finalPayload.time_trigger_business_hours_only =
          timeData.data.businessHoursOnly;
      }

      // Dispatch: keep isSubmitting = true until we get success or error from backend.
      if (isEdit && id) {
        // Update existing rule
        dispatch(loadEscalationRuleUpdateRequest(id, finalPayload));
      } else {
        // Create new rule
        dispatch(loadEscalationRuleAddRequest(finalPayload));
      }

      // Note: do NOT set setIsSubmitting(false) here — wait for addSuccess/updateSuccess or addError/updateError effects.
    } catch (validationException) {
      // Unexpected exception during validation/assembly: re-enable and show alert
      setIsSubmitting(false);
      dispatch(
        addErrorAlert(
          "An unexpected error occurred while validating. Please try again."
        )
      );
      console.error("handleSave exception:", validationException);
    }
  };

  //Navigate only when API call succeeds
  useEffect(() => {
    if (updateSuccess) {
      // reset state and navigate
      setIsSubmitting(false);
      dispatch(resetEscalationRuleUpdate());
      navigate("/escalationRules");
    }
    if (addSuccess) {
      setIsSubmitting(false);
      dispatch(resetEscalationRuleAdd());
      navigate("/escalationRules");
    }
  }, [updateSuccess, addSuccess, navigate, dispatch]);

  const handleCancel = () => {
    navigate("/escalationRules");
  };

  if (loading) {
    return <Loader />;
  }
  const initialData = ruleDetails?.data || null;

  return (
    <>
      <Row className="detailsPageHeader ">
        <Col>
          <Breadcrumbs
            children={[
              { href: "/escalationRules/", label: 'Escalation Rules' },
              {
                label: isEdit ? initialData?.rule_name : 'Creation',
              },
            ]}
          />
        </Col>
        <Col>
          <div className="float-end">
            {isEdit && (                
              <PrevNextButtons currentId={id} basePath={"/escalationRulesForm/"} name={"Escalation Rules"} />
            )}
            <Link to="/escalationRules">
              <DefaultButton
                buttonClass="default-btn-color"
                buttonTitle={BACKTOLIST}
              />
            </Link>
          </div>
        </Col>        
      </Row>
   <div className="page-container escalation">
      <div className="scrollable-area">
        <RuleConfiguration
          executionMode={executionMode}
          setExecutionMode={setExecutionMode}
          ref={ruleConfigRef}
          isEdit={isEdit}
          initialData={initialData}
        />
      </div>
      <div className="scrollable-area">
        <ConditionBuilder
          ref={conditionsRef}
          isEdit={isEdit}
          initialData={initialData?.additional_conditions}
        />
      </div>

      <div className="scrollable-area">
        <TimeTriggerConfig
          allowEnable={executionMode === "scheduled"}
          ref={timeTriggerRef}
          isEdit={isEdit}
          initialData={initialData}
        />
      </div>

      <div className="scrollable-area">
        <NotificationSettings
          ref={notificationRef}
          isEdit={isEdit}
          initialData={initialData}
        />
      </div>

      <div className="scrollable-area">
        <ActionsSection
          ref={actionsRef}
          isEdit={isEdit}
          initialActions={initialData?.actions || []}
        />
      </div>

      <div
        style={{ marginBottom: "80px", marginTop: "20px", marginRight: "10px" }}
      >
        <Button
          className="default-btn-color"
          style={{ float: "right" }}
          onClick={handleSave}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Spinner
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />{" "}
              Saving...
            </>
          ) : (
            "Save"
          )}
        </Button>
        <Button
          variant="secondary"
          style={{ float: "right", marginRight: "10px" }}
          onClick={handleCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
      </div>
    </div>
    </>
  );
}
